import React from 'react'
import { createNgReactBridgeDirective } from '../../react-bridge'
import { VulnerabilityDashboard } from '../../../components/vulnerability-dashboard'
import {
  defaultStatusRegistry,
  getDefaultSubstatusRegistry
} from '../../../components/vulnerability-status'
import {
  SubstatusName,
  Substatus
} from '../../../components/vulnerability-status/status'
import { Severity } from '../../../components/vulnerability-severity'
import {
  transformRole,
  PrefixedRole
} from '../../../graphql/schema/queries/helpers/role'
import { Role } from '@contrast-security-inc/base-types'

export type NgSeverityName = 'critical' | 'high' | 'medium' | 'low' | 'note'
export type SeverityName = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NOTE'
const severityNameMap = new Map<NgSeverityName, SeverityName>([
  ['critical', 'CRITICAL'],
  ['high', 'HIGH'],
  ['medium', 'MEDIUM'],
  ['low', 'LOW'],
  ['note', 'NOTE']
])

export type NgStatusName =
  | 'Reported'
  | 'Suspicious'
  | 'Confirmed'
  | 'Not a Problem'
  | 'NotAProblem'
  | 'Remediated'
  | 'Fixed'
  | 'Remediated - Auto-Verified'
type StatusName =
  | 'Reported'
  | 'Suspicious'
  | 'Confirmed'
  | 'NotAProblem'
  | 'Remediated'
  | 'Fixed'
  | 'AutoRemediated'
const statusNameMap = new Map<NgStatusName, StatusName>([
  ['Reported', 'Reported'],
  ['Suspicious', 'Suspicious'],
  ['Confirmed', 'Confirmed'],
  ['Not a Problem', 'NotAProblem'],
  ['NotAProblem', 'NotAProblem'],
  ['Remediated', 'Remediated'],
  ['Fixed', 'Fixed'],
  ['Remediated - Auto-Verified', 'AutoRemediated']
])

export interface Props {
  readonly applicationRoles?: PrefixedRole[]
  readonly organizationID: string
  readonly vulnerabilityID: string
  readonly statusName: NgStatusName
  readonly substatusName?: SubstatusName
  readonly substatus?: string
  readonly firstDetected: string
  readonly likelihoodName: NgSeverityName
  readonly impactName: NgSeverityName
  readonly severityName: NgSeverityName
  readonly defaultSeverityName?: NgSeverityName
  readonly refresh?: () => void
  readonly pendingStatusName?: NgStatusName
  readonly pendingStatusCreationTime?: string
}

const mapSeverityName = (key: string, val: string) => {
  const mapped = severityNameMap.get(val.toLowerCase() as NgSeverityName)
  if (!mapped) {
    throw new Error(`${key} is required`)
  }
  return mapped
}

const mapPendingStatus = (
  pendingStatusName?: NgStatusName,
  pendingStatusCreationTime?: string
) => {
  if (!pendingStatusName) {
    return undefined
  }
  if (!pendingStatusCreationTime) {
    return undefined
  }
  const mappedPendingStatusName = statusNameMap.get(pendingStatusName)
  if (!mappedPendingStatusName) {
    throw new Error('pendingStatusName is required')
  }
  return defaultStatusRegistry.fromName(mappedPendingStatusName)
}

const mapSubstatus = (substatusName: SubstatusName, substatus: string) => {
  const defaultSubstatus = getDefaultSubstatusRegistry().fromName(substatusName)

  if (substatusName === Substatus.OTHER.name) {
    return new Substatus(
      substatusName,
      substatus,
      substatus,
      defaultSubstatus.requiresJustification,
      substatus
    )
  }
  return defaultSubstatus
}

export function mapProps({
  statusName,
  substatusName,
  substatus,
  severityName,
  defaultSeverityName,
  impactName,
  likelihoodName,
  refresh,
  applicationRoles,
  pendingStatusName,
  pendingStatusCreationTime,
  ...props
}: Props) {
  const mappedSeverityName = mapSeverityName('severityName', severityName)
  const mappedDefaultSeverityName = !!defaultSeverityName
    ? mapSeverityName('defaultSeverityName', defaultSeverityName)
    : undefined
  const mappedImpactName = mapSeverityName('impactName', impactName)
  const mappedLikelihoodName = mapSeverityName('likelihoodName', likelihoodName)

  const mappedStatusName = statusNameMap.get(statusName)
  if (!mappedStatusName) {
    throw new Error('statusName is required')
  }

  const mappedPendingStatus = mapPendingStatus(
    pendingStatusName,
    pendingStatusCreationTime
  )

  return {
    pendingStatus: mappedPendingStatus,
    severity: Severity.fromName(mappedSeverityName),
    defaultSeverity: !!mappedDefaultSeverityName
      ? Severity.fromName(mappedDefaultSeverityName)
      : undefined,
    impact: Severity.fromName(mappedImpactName),
    likelihood: Severity.fromName(mappedLikelihoodName),
    status: defaultStatusRegistry.fromName(mappedStatusName),
    substatus:
      substatusName && substatus
        ? mapSubstatus(substatusName, substatus)
        : undefined,
    role: !!applicationRoles
      ? (transformRole(applicationRoles) as Role)
      : undefined,
    refresh: !!refresh
      ? () => {
          refresh()
          return Promise.resolve()
        }
      : undefined,
    ...props
  }
}

export function BridgeComponent(props: Props) {
  return <VulnerabilityDashboard {...mapProps(props)} />
}

export const vulnerabilityDetails = createNgReactBridgeDirective(
  'reactVulnerabilityDetails',
  [],
  BridgeComponent,
  [
    'organizationID',
    'applicationRoles',
    'vulnerabilityID',
    'statusName',
    'substatusName',
    'substatus',
    'applicationId',
    'vulnerabilityType',
    'likelihoodName',
    'impactName',
    'firstDetected',
    'severityName',
    'defaultSeverityName',
    'refresh',
    'pendingStatusName',
    'pendingStatusCreationTime'
  ]
)
